import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import CloseIcon from '@material-ui/icons/Close';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import WIRField from 'pages/WellGuardian/WIR/components/WIRField';
import { getRoleName } from 'pages/WellGuardian/WIR/utils';
import { getAssetsByCountry } from 'pages/WellGuardian/WIR/utils/getAssetsByCountry';
import React from 'react';
import { useIntl } from 'react-intl';
import { useCountryState } from 'redux/WellGuardian/selectors';
import {
  AssetsAg,
  AssetsDk,
  AssetsNg,
  AssetsUk,
  WIRTransverseRoles,
  WIRUser,
} from 'types/wellGuardian';

import styles from './UserDetailsModal.module.scss';
import { UserMode } from '../../userRoleSettingsModal.constants';
import { getAllWirRoles } from '../../utils';

const ALL_WIR = 'ALL';
interface UserDetailsModalProps {
  modalOpen: boolean;
  okMethod: () => void;
  cancelMethod: () => void;
  setUser: (user: WIRUser) => void;
  user: WIRUser;
  mode: UserMode | undefined;
}

const getFieldValue = (value: string | undefined) => value ?? '';

const getAssetFilter = (user: WIRUser, country: string) => {
  const countryAsset = getAssetsByCountry(country);

  const handleCursor = (user: any, eventKey: any) => {
    if (user.assets?.includes(eventKey)) {
      return 'pointer';
    }

    return user.assets?.includes('ALL') ? 'not-allowed' : 'pointer';
  };

  const handleColor = (user: any, eventKey: any) => {
    if (user.assets?.includes(eventKey)) {
      return styles.enableClr;
    }

    return user.assets?.includes('ALL') ? styles.disableClr : styles.enableClr;
  };

  return Object.keys(countryAsset).map((eventKey, idx) => (
    <MenuItem
      dense
      value={eventKey}
      key={eventKey}
      style={{
        background: user.assets?.includes(eventKey) ? styles.selectedMenuColor : undefined,
        cursor: handleCursor(user, eventKey),
        color: handleColor(user, eventKey),
      }}
    >
      {Object.values(countryAsset)[idx]}
    </MenuItem>
  ));
};

const UserDetailsModal: React.FunctionComponent<UserDetailsModalProps> = ({
  modalOpen,
  okMethod,
  cancelMethod,
  setUser,
  user,
  mode,
}) => {
  const intl = useIntl();
  const countrySelect = useCountryState();

  const getAllRequiredFieldsFilled = () =>
    !isEmpty(user.userId) &&
    !isEmpty(user.userName) &&
    !isEmpty(user.emailAddress) &&
    !isEmpty(user.roles);

  const handelAssetChange = (value: any) => {
    if (value.includes(ALL_WIR) && value.length !== 0) {
      value = [ALL_WIR];
    }

    return setUser({
      ...user,
      assets: value as AssetsUk[] | AssetsDk[] | AssetsNg[] | AssetsAg[],
    });
  };

  return (
    <Dialog
      aria-labelledby="user-details-dialog"
      open={modalOpen}
      maxWidth="md"
      scroll="body"
      classes={{ paperWidthMd: styles.paperWidthMd, root: styles.root }}
      data-testid="user-details-dialog"
    >
      <MuiDialogTitle disableTypography className={styles.titleDialog}>
        <Typography variant="h6" className={styles.titleText}>
          {mode === 'add' &&
            intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.addNewUser' })}
          {mode === 'edit' &&
            intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.editUser' })}
        </Typography>
        <IconButton aria-label="close" className={styles.closeButton} onClick={cancelMethod}>
          <CloseIcon />
        </IconButton>
      </MuiDialogTitle>
      <MuiDialogContent classes={{ root: styles.contentRoot }}>
        <WIRField
          id="outlined-userId-field"
          label={intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.userId' })}
          type="search"
          value={getFieldValue(user.userId)}
          onChange={event => setUser({ ...user, userId: event.target.value })}
          disabled={mode === 'edit'}
          required
        />
        <WIRField
          id="outlined-userName-field"
          label={intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.name' })}
          type="search"
          value={getFieldValue(user.userName)}
          onChange={event => setUser({ ...user, userName: event.target.value })}
          required
        />
        <WIRField
          id="outlined-emailAddress-field"
          label={intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.emailAddress' })}
          type="search"
          value={getFieldValue(user.emailAddress?.toLowerCase())}
          onChange={event => setUser({ ...user, emailAddress: event.target.value?.toLowerCase() })}
          required
        />
        <WIRField
          id="outlined-asset-field"
          label={intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.asset' })}
          SelectProps={{
            MenuProps: {
              getContentAnchorEl: null,
            },
            multiple: true,
            value: user.assets ?? [],
            onChange: event => {
              handelAssetChange(event?.target?.value);
            },
          }}
          select
        >
          {getAssetFilter(user, countrySelect)}
        </WIRField>
        <WIRField
          id="outlined-roles-field"
          label={intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.roles' })}
          SelectProps={{
            MenuProps: {
              getContentAnchorEl: null,
            },
            multiple: true,
            value: user.roles ?? [],
            onChange: event =>
              setUser({
                ...user,
                roles: uniq([
                  WIRTransverseRoles.READ_ONLY_WIR_USER,
                  ...(event.target.value as WIRTransverseRoles[]),
                ]),
              }),
          }}
          select
          required
        >
          {getAllWirRoles().map(role => (
            <MenuItem
              dense
              value={role}
              key={role}
              style={{
                background: user.roles?.includes(role as WIRTransverseRoles)
                  ? styles.selectedMenuColor
                  : undefined,
              }}
            >
              {getRoleName(role)}
            </MenuItem>
          ))}
        </WIRField>
      </MuiDialogContent>
      <MuiDialogActions>
        <Button onClick={cancelMethod} className={styles.cancelButton}>
          {intl.formatMessage({
            id: 'wellGuardian.wir.createNewWIRModal.cancel',
          })}
        </Button>
        <Button
          onClick={okMethod}
          className={styles.okButton}
          disabled={!getAllRequiredFieldsFilled()}
        >
          {intl.formatMessage({
            id: 'wellGuardian.wir.createNewWIRModal.saveAndExit',
          })}
          <ArrowForwardIosIcon fontSize="small" className={styles.buttonIcon} />
        </Button>
      </MuiDialogActions>
    </Dialog>
  );
};

export default UserDetailsModal;
